import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import {
  Box, Typography, Stack, Button, Select, MenuItem,
  FormControl, InputLabel, TextField, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  CircularProgress, Chip, Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { timelineApi } from '../../api/timelineApi';
import { useV4Resolve } from '../theme';

const PHASE_DEFS = {
  seedling:    { label: 'Keimung',   color: '#39ff14', defaultWeeks: 2  },
  veg:         { label: 'Wachstum',  color: '#00d4ff', defaultWeeks: 8  },
  pre_flower:  { label: 'Vorblüte',  color: '#ff9500', defaultWeeks: 2  },
  flower:      { label: 'Blüte',     color: '#ff3b30', defaultWeeks: 8  },
  late_flower: { label: 'Spätblüte', color: '#ff2d55', defaultWeeks: 4  },
  flush:       { label: 'Spülung',   color: '#5ac8fa', defaultWeeks: 2  },
  dry:         { label: 'Trocknung', color: '#a0a0a0', defaultWeeks: 2  },
};

const PRESET_TIMELINE = [
  { phase_type: 'seedling',   week_count: 2  },
  { phase_type: 'veg',        week_count: 8  },
  { phase_type: 'pre_flower', week_count: 2  },
  { phase_type: 'flower',     week_count: 8  },
  { phase_type: 'flush',      week_count: 2  },
];

const V4_CARD = {
  background: 'rgba(var(--v4-surface-rgb),0.9)',
  border: '1px solid rgba(var(--v4-cyan-rgb),0.12)',
  borderRadius: '14px',
};

function phaseDef(type) {
  return PHASE_DEFS[type] || { label: type, color: '#888', defaultWeeks: 4 };
}

export default function GrowPlanTimelineView({ growSetupId, userId }) {
  const resolve = useV4Resolve();
  const [timeline, setTimeline] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [blockEditorOpen, setBlockEditorOpen] = useState(false);
  const [blockDraft, setBlockDraft] = useState(null);

  const [profileEditorOpen, setProfileEditorOpen] = useState(false);
  const [profileDraft, setProfileDraft] = useState(null);

  const [addBlockOpen, setAddBlockOpen] = useState(false);
  const [newBlockPhase, setNewBlockPhase] = useState('veg');

  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState(null);
  const [cloneName, setCloneName] = useState('');

  // start date state
  const [startDateDialogOpen, setStartDateDialogOpen] = useState(false);
  const [startDateValue, setStartDateValue] = useState(new Date().toISOString().split('T')[0]);
  const [currentPhase, setCurrentPhase] = useState(null);

  // drag state
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [ghostPos, setGhostPos] = useState({ x: 0, y: 0 });

  // resize state
  const [resizeBlockId, setResizeBlockId] = useState(null);
  const resizeRef = useRef({ startX: 0, startWeeks: 0, pixPerWeek: 0 });
  const trackRef = useRef(null);

  const totalWeeks = blocks.reduce((s, b) => s + (b.week_count || 1), 0) || 1;

  // ── Load ────────────────────────────────────────────────────────────────

  const loadProfiles = useCallback(async () => {
    try {
      const d = await timelineApi.listProfiles(userId);
      setProfiles(d.profiles || []);
    } catch (_) {}
  }, [userId]);

  const loadTimeline = useCallback(async () => {
    if (!growSetupId) return;
    setLoading(true);
    try {
      const listData = await timelineApi.listTimelines(growSetupId);
      let tl = (listData.timelines || [])[0];
      if (!tl) {
        const created = await timelineApi.createTimeline({
          grow_setup_id: growSetupId,
          name: 'Mein Grow-Plan',
        });
        tl = created.timeline;
      } else {
        const full = await timelineApi.getTimeline(tl.id);
        tl = full.timeline;
      }
      setTimeline(tl);
      setBlocks(tl.blocks || []);
      if (tl.start_date && tl.id) {
        timelineApi.getCurrentPhase(tl.id).then(d => setCurrentPhase(d)).catch(() => {});
      } else {
        setCurrentPhase(null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [growSetupId]);

  useEffect(() => { loadTimeline(); }, [loadTimeline]);
  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  // ── Drag-to-reorder ─────────────────────────────────────────────────────

  const handleBlockPointerDown = (e, idx) => {
    if (e.target.dataset.resize) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragIdx(idx);
    setDragOverIdx(idx);
    setGhostPos({ x: e.clientX, y: e.clientY });
  };

  const handleTrackPointerMove = useCallback((e) => {
    if (resizeBlockId !== null) {
      const track = trackRef.current;
      if (!track) return;
      const trackW = track.getBoundingClientRect().width;
      const pixPerWeek = trackW / totalWeeks;
      const delta = e.clientX - resizeRef.current.startX;
      const deltaWeeks = Math.round(delta / pixPerWeek);
      const newCount = Math.max(1, resizeRef.current.startWeeks + deltaWeeks);
      setBlocks(prev => prev.map(b =>
        b.id === resizeBlockId ? { ...b, week_count: newCount } : b
      ));
      return;
    }
    if (dragIdx === null) return;
    setGhostPos({ x: e.clientX, y: e.clientY });
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const xRel = e.clientX - rect.left;
    const trackW = rect.width;
    let acc = 0;
    let overIdx = blocks.length - 1;
    for (let i = 0; i < blocks.length; i++) {
      const w = (blocks[i].week_count / totalWeeks) * trackW;
      if (xRel < acc + w / 2) { overIdx = i; break; }
      if (xRel < acc + w) { overIdx = i + 1 < blocks.length ? i + 1 : i; break; }
      acc += w;
    }
    setDragOverIdx(overIdx);
  }, [dragIdx, resizeBlockId, blocks, totalWeeks]);

  const handleTrackPointerUp = useCallback(async () => {
    if (resizeBlockId !== null) {
      const b = blocks.find(x => x.id === resizeBlockId);
      if (b) {
        try {
          await timelineApi.updateBlock(timeline.id, b.id, { week_count: b.week_count });
        } catch (_) {}
      }
      setResizeBlockId(null);
      return;
    }
    if (dragIdx === null) return;
    const fromIdx = dragIdx;
    const toIdx = dragOverIdx;
    setDragIdx(null);
    setDragOverIdx(null);
    if (fromIdx === toIdx) return;

    const reordered = [...blocks];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    const withPos = reordered.map((b, i) => ({ ...b, position: i }));
    setBlocks(withPos);
    try {
      await timelineApi.reorderBlocks(
        timeline.id,
        withPos.map(b => ({ id: b.id, position: b.position }))
      );
    } catch (_) {
      loadTimeline();
    }
  }, [dragIdx, dragOverIdx, resizeBlockId, blocks, timeline, loadTimeline]);

  const handleResizeDown = (e, block) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const track = trackRef.current;
    const trackW = track ? track.getBoundingClientRect().width : 1000;
    resizeRef.current = {
      startX: e.clientX,
      startWeeks: block.week_count,
      pixPerWeek: trackW / totalWeeks,
    };
    setResizeBlockId(block.id);
  };

  // ── Block editor ─────────────────────────────────────────────────────────

  const openBlockEditor = (block) => {
    setBlockDraft({ ...block });
    setBlockEditorOpen(true);
  };

  const handleSaveBlock = async () => {
    if (!blockDraft) return;
    setSaving(true);
    try {
      const res = await timelineApi.updateBlock(timeline.id, blockDraft.id, {
        phase_type: blockDraft.phase_type,
        label: blockDraft.label || null,
        week_count: Math.max(1, parseInt(blockDraft.week_count) || 1),
        color: blockDraft.color || null,
        profile_id: blockDraft.profile_id || null,
      });
      setBlocks(prev => prev.map(b => b.id === res.block.id ? res.block : b));
      setBlockEditorOpen(false);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm('Block löschen?')) return;
    try {
      await timelineApi.deleteBlock(timeline.id, blockId);
      setBlocks(prev => prev.filter(b => b.id !== blockId).map((b, i) => ({ ...b, position: i })));
      setBlockEditorOpen(false);
    } catch (e) {
      alert(e.message);
    }
  };

  const handleAddBlock = async () => {
    if (!timeline) return;
    const def = phaseDef(newBlockPhase);
    try {
      const res = await timelineApi.addBlock(timeline.id, {
        phase_type: newBlockPhase,
        week_count: def.defaultWeeks,
        color: def.color,
      });
      setBlocks(prev => [...prev, res.block]);
      setAddBlockOpen(false);
    } catch (e) {
      alert(e.message);
    }
  };

  const handleLoadPreset = async () => {
    if (!timeline) return;
    if (!window.confirm('Alle bestehenden Blöcke ersetzen?')) return;
    for (const b of blocks) {
      try { await timelineApi.deleteBlock(timeline.id, b.id); } catch (_) {}
    }
    const newBlocks = [];
    for (let i = 0; i < PRESET_TIMELINE.length; i++) {
      const pt = PRESET_TIMELINE[i];
      const def = phaseDef(pt.phase_type);
      try {
        const res = await timelineApi.addBlock(timeline.id, {
          phase_type: pt.phase_type,
          week_count: pt.week_count,
          color: def.color,
        });
        newBlocks.push(res.block);
      } catch (_) {}
    }
    setBlocks(newBlocks);
    setPresetDialogOpen(false);
  };

  // ── Profile editor ───────────────────────────────────────────────────────

  const openProfileEditor = async (profileId) => {
    if (!profileId) return;
    try {
      const res = await timelineApi.getProfile(profileId);
      setProfileDraft(res.profile);
      setProfileEditorOpen(true);
    } catch (e) {
      alert(e.message);
    }
  };

  const openCloneDialog = (profileId) => {
    const prof = profiles.find(p => p.id === profileId);
    setCloneSourceId(profileId);
    setCloneName(prof ? `${prof.name} (Kopie)` : 'Neues Profil');
    setCloneDialogOpen(true);
  };

  const handleCloneProfile = async () => {
    try {
      const res = await timelineApi.cloneProfile(cloneSourceId, { user_id: userId, name: cloneName });
      setProfiles(prev => [...prev, res.profile]);
      setCloneDialogOpen(false);
      setProfileDraft(res.profile);
      setProfileEditorOpen(true);
    } catch (e) {
      alert(e.message);
    }
  };

  const handleSetStartDate = async () => {
    if (!startDateValue || !timeline) return;
    try {
      const res = await timelineApi.startTimeline(timeline.id, startDateValue);
      setTimeline(res.timeline);
      setBlocks(res.timeline.blocks || []);
      setStartDateDialogOpen(false);
      if (res.timeline.start_date) {
        timelineApi.getCurrentPhase(res.timeline.id).then(d => setCurrentPhase(d)).catch(() => {});
      }
    } catch (e) { alert(e.message); }
  };

  const handleClearStartDate = async () => {
    if (!timeline) return;
    try {
      const res = await timelineApi.startTimeline(timeline.id, null);
      setTimeline(res.timeline);
      setBlocks(res.timeline.blocks || []);
      setCurrentPhase(null);
    } catch (e) { alert(e.message); }
  };

  const handleSaveProfile = async () => {
    if (!profileDraft) return;
    setSaving(true);
    try {
      const res = await timelineApi.updateProfile(profileDraft.id, {
        name: profileDraft.name,
        description: profileDraft.description,
        weeks: profileDraft.weeks,
      });
      setProfiles(prev => prev.map(p => p.id === res.profile.id ? res.profile : p));
      setProfileEditorOpen(false);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const updateWeekField = (weekIdx, field, value) => {
    setProfileDraft(prev => {
      const weeks = prev.weeks.map((w, i) => i === weekIdx ? { ...w, [field]: value } : w);
      return { ...prev, weeks };
    });
  };

  const updateProductField = (weekIdx, prodIdx, field, value) => {
    setProfileDraft(prev => {
      const weeks = prev.weeks.map((w, wi) => {
        if (wi !== weekIdx) return w;
        const products = w.products.map((p, pi) => pi === prodIdx ? { ...p, [field]: value } : p);
        return { ...w, products };
      });
      return { ...prev, weeks };
    });
  };

  const addProduct = (weekIdx) => {
    setProfileDraft(prev => {
      const weeks = prev.weeks.map((w, wi) => {
        if (wi !== weekIdx) return w;
        return { ...w, products: [...w.products, { product_name: '', ml_per_l: 0, sort_order: w.products.length }] };
      });
      return { ...prev, weeks };
    });
  };

  const removeProduct = (weekIdx, prodIdx) => {
    setProfileDraft(prev => {
      const weeks = prev.weeks.map((w, wi) => {
        if (wi !== weekIdx) return w;
        return { ...w, products: w.products.filter((_, pi) => pi !== prodIdx) };
      });
      return { ...prev, weeks };
    });
  };

  const addWeek = () => {
    setProfileDraft(prev => ({
      ...prev,
      weeks: [...prev.weeks, {
        week_number: prev.weeks.length + 1,
        target_ec: 1.0,
        target_ph: 6.0,
        water_l: 2.0,
        note: '',
        products: [],
      }],
    }));
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: 'var(--v4-cyan)' }} />
      </Box>
    );
  }

  const selectedBlock = blocks.find(b => b.id === selectedBlockId);

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header */}
      <Box sx={{ ...V4_CARD, p: 2.5, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: 'var(--v4-text)' }}>
              {timeline?.name || 'Grow-Plan Timeline'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(var(--v4-cyan-rgb),0.5)' }}>
              {blocks.length} Phasen · {totalWeeks} Wochen gesamt
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              size="small" variant="outlined"
              onClick={() => setPresetDialogOpen(true)}
              sx={{ borderColor: 'rgba(var(--v4-cyan-rgb),0.3)', color: 'var(--v4-cyan)', borderRadius: 2 }}
            >
              Standard laden
            </Button>
            <Button
              size="small" variant="contained" startIcon={<AddIcon />}
              onClick={() => setAddBlockOpen(true)}
              sx={{ borderRadius: 2, background: 'linear-gradient(135deg,var(--v4-cyan),var(--v4-cyan-deep))' }}
            >
              Phase hinzufügen
            </Button>
            {timeline?.start_date ? (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Chip
                  label={`▶ ${new Date(timeline.start_date + 'T00:00:00').toLocaleDateString('de-DE')}`}
                  sx={{ background: 'rgba(var(--v4-green-rgb),0.15)', border: '1px solid rgba(var(--v4-green-rgb),0.3)', color: 'var(--v4-green)', fontWeight: 700, fontSize: '0.7rem' }}
                />
                {currentPhase?.active_block && (
                  <Chip
                    label={`Woche ${currentPhase.current_week}`}
                    size="small"
                    sx={{ background: 'rgba(var(--v4-green-rgb),0.08)', border: '1px solid rgba(var(--v4-green-rgb),0.2)', color: 'var(--v4-green)', fontSize: '0.65rem' }}
                  />
                )}
                <Tooltip title="Startdatum entfernen">
                  <IconButton size="small" onClick={handleClearStartDate} sx={{ color: 'rgba(var(--v4-red-soft-rgb),0.6)', p: 0.5 }}>
                    <CloseIcon sx={{ fontSize: '0.85rem' }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : (
              <Button
                size="small" variant="outlined"
                onClick={() => setStartDateDialogOpen(true)}
                sx={{ borderColor: 'rgba(var(--v4-green-rgb),0.3)', color: 'var(--v4-green)', borderRadius: 2, fontSize: '0.75rem' }}
              >
                Grow starten
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Ruler + Timeline track */}
      <Box sx={{ ...V4_CARD, p: 2, mb: 2, userSelect: 'none' }}>
        {/* Ruler */}
        <Box sx={{ position: 'relative', height: 20, mb: 0.5 }}>
          {Array.from({ length: totalWeeks }, (_, i) => i + 1).map(w => (
            (w === 1 || w % 2 === 0) ? (
              <Box
                key={w}
                sx={{
                  position: 'absolute',
                  left: `${((w - 1) / totalWeeks) * 100}%`,
                  top: 0,
                  transform: 'translateX(-50%)',
                }}
              >
                <Typography sx={{ fontSize: '0.58rem', color: 'rgba(var(--v4-cyan-rgb),0.4)', lineHeight: 1 }}>
                  W{w}
                </Typography>
              </Box>
            ) : null
          ))}
        </Box>

        {/* Track */}
        <Box
          ref={trackRef}
          onPointerMove={handleTrackPointerMove}
          onPointerUp={handleTrackPointerUp}
          sx={{
            display: 'flex',
            height: 80,
            borderRadius: '10px',
            overflow: 'hidden',
            position: 'relative',
            border: '1px solid rgba(var(--v4-cyan-rgb),0.1)',
            cursor: dragIdx !== null ? 'grabbing' : 'default',
          }}
        >
          {blocks.map((block, idx) => {
            const def = phaseDef(block.phase_type);
            const color = resolve(block.color || def.color);
            const pct = (block.week_count / totalWeeks) * 100;
            const isDragging = dragIdx === idx;
            const isDropTarget = dragOverIdx === idx && dragIdx !== null && dragIdx !== idx;
            const isActivePhase = currentPhase?.active_block?.id === block.id;

            return (
              <Box
                key={block.id}
                onPointerDown={(e) => handleBlockPointerDown(e, idx)}
                onClick={() => {
                  if (dragIdx === null && resizeBlockId === null) {
                    setSelectedBlockId(block.id);
                    openBlockEditor(block);
                  }
                }}
                sx={{
                  flex: `0 0 ${pct}%`,
                  minWidth: 40,
                  position: 'relative',
                  background: `linear-gradient(135deg, color-mix(in srgb, ${color} 20%, transparent), color-mix(in srgb, ${color} 9%, transparent))`,
                  borderLeft: isDropTarget ? '3px solid var(--v4-cyan)' : `2px solid color-mix(in srgb, ${color} 40%, transparent)`,
                  boxShadow: isActivePhase ? 'inset 0 0 24px rgba(var(--v4-green-rgb),0.12)' : 'none',
                  outline: isActivePhase ? '2px solid rgba(var(--v4-green-rgb),0.4)' : 'none',
                  outlineOffset: '-2px',
                  opacity: isDragging ? 0.45 : 1,
                  cursor: dragIdx !== null ? 'grabbing' : 'grab',
                  transition: isDragging ? 'none' : 'opacity 0.15s',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  '&:hover': { background: `linear-gradient(135deg, color-mix(in srgb, ${color} 27%, transparent), color-mix(in srgb, ${color} 13%, transparent))` },
                }}
              >
                <Box
                  sx={{
                    width: 8, height: '80%', minHeight: 20,
                    position: 'absolute', left: 0, top: '10%',
                    background: `color-mix(in srgb, ${color} 53%, transparent)`,
                    borderRadius: '3px 0 0 3px',
                  }}
                />
                <Typography sx={{
                  fontSize: pct < 10 ? '0.52rem' : '0.68rem',
                  fontWeight: 800,
                  color: 'var(--v4-text)',
                  textAlign: 'center',
                  lineHeight: 1.2,
                  px: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '90%',
                }}>
                  {block.label || def.label}
                </Typography>
                <Typography sx={{ fontSize: '0.55rem', color: `color-mix(in srgb, ${color} 80%, transparent)`, lineHeight: 1 }}>
                  {block.week_count}W
                </Typography>
                {isActivePhase && (
                  <Box sx={{
                    position: 'absolute', top: 5, right: 16,
                    width: 7, height: 7, borderRadius: '50%',
                    background: 'var(--v4-green)',
                    boxShadow: '0 0 8px var(--v4-green), 0 0 3px var(--v4-green)',
                  }} />
                )}
                {/* Resize handle */}
                <Box
                  data-resize="1"
                  onPointerDown={(e) => handleResizeDown(e, block)}
                  sx={{
                    position: 'absolute', right: 0, top: 0, bottom: 0,
                    width: 8,
                    cursor: 'col-resize',
                    background: `color-mix(in srgb, ${color} 33%, transparent)`,
                    '&:hover': { background: `color-mix(in srgb, ${color} 80%, transparent)` },
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Box sx={{ width: 2, height: '60%', background: `${color}`, borderRadius: 1 }} />
                </Box>
              </Box>
            );
          })}

          {blocks.length === 0 && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: 'rgba(var(--v4-cyan-rgb),0.3)', fontSize: '0.8rem' }}>
                Klicke „Phase hinzufügen" oder lade einen Standard-Plan
              </Typography>
            </Box>
          )}
        </Box>

        {/* Ghost while dragging */}
        {dragIdx !== null && blocks[dragIdx] && (
          <Box sx={{
            position: 'fixed',
            left: ghostPos.x + 12,
            top: ghostPos.y - 20,
            pointerEvents: 'none',
            zIndex: 9999,
            background: `color-mix(in srgb, ${resolve(blocks[dragIdx].color || phaseDef(blocks[dragIdx].phase_type).color)} 33%, transparent)`,
            border: `2px solid ${resolve(blocks[dragIdx].color || phaseDef(blocks[dragIdx].phase_type).color)}`,
            borderRadius: '8px',
            px: 2, py: 0.5,
            minWidth: 60,
          }}>
            <Typography sx={{ fontSize: '0.72rem', color: 'var(--v4-text)', fontWeight: 800 }}>
              {blocks[dragIdx].label || phaseDef(blocks[dragIdx].phase_type).label}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Phase legend */}
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
        {blocks.map(b => {
          const def = phaseDef(b.phase_type);
          const color = resolve(b.color || def.color);
          return (
            <Chip
              key={b.id}
              label={`${b.label || def.label} (${b.week_count}W)`}
              size="small"
              onClick={() => openBlockEditor(b)}
              sx={{
                background: `color-mix(in srgb, ${color} 13%, transparent)`,
                border: `1px solid color-mix(in srgb, ${color} 33%, transparent)`,
                color: 'var(--v4-text)',
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: 2,
                cursor: 'pointer',
              }}
            />
          );
        })}
      </Stack>

      {/* Block editor dialog */}
      <Dialog
        open={blockEditorOpen} onClose={() => setBlockEditorOpen(false)}
        fullWidth maxWidth="sm"
        PaperProps={{ sx: { ...V4_CARD, backdropFilter: 'blur(20px)' } }}
      >
        <DialogTitle sx={{ color: 'var(--v4-text)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Phase bearbeiten
          <IconButton onClick={() => setBlockEditorOpen(false)} sx={{ color: 'rgba(var(--v4-muted-rgb),0.4)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {blockDraft && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: 'rgba(var(--v4-cyan-rgb),0.6)' }}>Phasen-Typ</InputLabel>
                <Select
                  label="Phasen-Typ"
                  value={blockDraft.phase_type}
                  onChange={e => setBlockDraft(d => ({ ...d, phase_type: e.target.value }))}
                  sx={{ color: 'var(--v4-text)', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.2)' } }}
                >
                  {Object.entries(PHASE_DEFS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Label (optional)" size="small"
                value={blockDraft.label || ''}
                onChange={e => setBlockDraft(d => ({ ...d, label: e.target.value }))}
                InputLabelProps={{ sx: { color: 'rgba(var(--v4-cyan-rgb),0.6)' } }}
                inputProps={{ style: { color: 'var(--v4-text)' } }}
                sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.2)' } }}
              />

              <TextField
                label="Wochen" size="small" type="number"
                value={blockDraft.week_count}
                onChange={e => setBlockDraft(d => ({ ...d, week_count: Math.max(1, parseInt(e.target.value) || 1) }))}
                inputProps={{ min: 1, max: 52, style: { color: 'var(--v4-text)' } }}
                InputLabelProps={{ sx: { color: 'rgba(var(--v4-cyan-rgb),0.6)' } }}
                sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.2)' } }}
              />

              {/* Color picker chips */}
              <Box>
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(var(--v4-cyan-rgb),0.6)', mb: 0.75 }}>Farbe</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {['#00d4ff','#39ff14','#ff3b30','#ff9500','#ff2d55','#5ac8fa','#a0a0a0','#af52de','#ffd60a'].map(c => (
                    <Box
                      key={c}
                      onClick={() => setBlockDraft(d => ({ ...d, color: c }))}
                      sx={{
                        width: 24, height: 24, borderRadius: '50%', background: resolve(c),
                        cursor: 'pointer',
                        border: blockDraft.color === c ? '2px solid var(--v4-text)' : '2px solid transparent',
                        boxShadow: blockDraft.color === c ? `0 0 8px ${resolve(c)}` : 'none',
                      }}
                    />
                  ))}
                </Stack>
              </Box>

              <Divider sx={{ borderColor: 'rgba(var(--v4-cyan-rgb),0.1)' }} />

              {/* Profile selector */}
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: 'rgba(var(--v4-cyan-rgb),0.6)' }}>Nährstoffprofil</InputLabel>
                <Select
                  label="Nährstoffprofil"
                  value={blockDraft.profile_id || ''}
                  onChange={e => setBlockDraft(d => ({ ...d, profile_id: e.target.value || null }))}
                  sx={{ color: 'var(--v4-text)', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.2)' } }}
                >
                  <MenuItem value=""><em>Kein Profil</em></MenuItem>
                  {profiles.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.is_preset ? '⭐ ' : ''}{p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {blockDraft.profile_id && (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small" startIcon={<EditIcon />} variant="outlined"
                    onClick={() => {
                      const prof = profiles.find(p => p.id === blockDraft.profile_id);
                      if (prof?.is_preset) {
                        openCloneDialog(blockDraft.profile_id);
                      } else {
                        openProfileEditor(blockDraft.profile_id);
                      }
                    }}
                    sx={{ borderColor: 'rgba(var(--v4-cyan-rgb),0.3)', color: 'var(--v4-cyan)', borderRadius: 2, fontSize: '0.72rem' }}
                  >
                    {profiles.find(p => p.id === blockDraft.profile_id)?.is_preset
                      ? 'Als Kopie bearbeiten'
                      : 'Profil bearbeiten'}
                  </Button>
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button
            color="error" variant="outlined" size="small"
            startIcon={<DeleteIcon />}
            onClick={() => blockDraft && handleDeleteBlock(blockDraft.id)}
            sx={{ borderRadius: 2 }}
          >
            Löschen
          </Button>
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setBlockEditorOpen(false)} variant="outlined" size="small" sx={{ borderRadius: 2, borderColor: 'rgba(var(--v4-cyan-rgb),0.2)', color: 'rgba(var(--v4-muted-rgb),0.7)' }}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveBlock} variant="contained" size="small" disabled={saving} sx={{ borderRadius: 2, background: 'linear-gradient(135deg,var(--v4-cyan),var(--v4-cyan-deep))' }}>
              {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Speichern'}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Add block dialog */}
      <Dialog
        open={addBlockOpen} onClose={() => setAddBlockOpen(false)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { ...V4_CARD, backdropFilter: 'blur(20px)' } }}
      >
        <DialogTitle sx={{ color: 'var(--v4-text)' }}>Phase hinzufügen</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel sx={{ color: 'rgba(var(--v4-cyan-rgb),0.6)' }}>Phasen-Typ</InputLabel>
            <Select
              label="Phasen-Typ" value={newBlockPhase}
              onChange={e => setNewBlockPhase(e.target.value)}
              sx={{ color: 'var(--v4-text)', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.2)' } }}
            >
              {Object.entries(PHASE_DEFS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAddBlockOpen(false)} variant="outlined" size="small" sx={{ borderRadius: 2, borderColor: 'rgba(var(--v4-cyan-rgb),0.2)', color: 'rgba(var(--v4-muted-rgb),0.7)' }}>Abbrechen</Button>
          <Button onClick={handleAddBlock} variant="contained" size="small" sx={{ borderRadius: 2, background: 'linear-gradient(135deg,var(--v4-cyan),var(--v4-cyan-deep))' }}>Hinzufügen</Button>
        </DialogActions>
      </Dialog>

      {/* Preset confirmation dialog */}
      <Dialog
        open={presetDialogOpen} onClose={() => setPresetDialogOpen(false)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { ...V4_CARD, backdropFilter: 'blur(20px)' } }}
      >
        <DialogTitle sx={{ color: 'var(--v4-text)' }}>Standard Cannabis-Timeline laden</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'rgba(var(--v4-muted-rgb),0.7)', mb: 2 }}>
            Lädt einen typischen Cannabis-Grow-Plan:
          </Typography>
          <Stack spacing={0.5}>
            {PRESET_TIMELINE.map((pt, i) => {
              const def = phaseDef(pt.phase_type);
              return (
                <Stack key={i} direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: resolve(def.color) }} />
                  <Typography sx={{ color: 'var(--v4-text)', fontSize: '0.82rem' }}>
                    {def.label} — {pt.week_count} Wochen
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
          <Typography variant="caption" sx={{ color: 'rgba(var(--v4-red-soft-rgb),0.7)', mt: 1.5, display: 'block' }}>
            Bestehende Blöcke werden überschrieben.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPresetDialogOpen(false)} variant="outlined" size="small" sx={{ borderRadius: 2, borderColor: 'rgba(var(--v4-cyan-rgb),0.2)', color: 'rgba(var(--v4-muted-rgb),0.7)' }}>Abbrechen</Button>
          <Button onClick={handleLoadPreset} variant="contained" size="small" sx={{ borderRadius: 2, background: 'linear-gradient(135deg,var(--v4-cyan),var(--v4-cyan-deep))' }}>Laden</Button>
        </DialogActions>
      </Dialog>

      {/* Clone profile dialog */}
      <Dialog
        open={cloneDialogOpen} onClose={() => setCloneDialogOpen(false)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { ...V4_CARD, backdropFilter: 'blur(20px)' } }}
      >
        <DialogTitle sx={{ color: 'var(--v4-text)' }}>Profil kopieren &amp; bearbeiten</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth size="small" label="Name der Kopie"
            value={cloneName}
            onChange={e => setCloneName(e.target.value)}
            sx={{ mt: 1, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.2)' } }}
            InputLabelProps={{ sx: { color: 'rgba(var(--v4-cyan-rgb),0.6)' } }}
            inputProps={{ style: { color: 'var(--v4-text)' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCloneDialogOpen(false)} variant="outlined" size="small" sx={{ borderRadius: 2, borderColor: 'rgba(var(--v4-cyan-rgb),0.2)', color: 'rgba(var(--v4-muted-rgb),0.7)' }}>Abbrechen</Button>
          <Button onClick={handleCloneProfile} variant="contained" size="small" startIcon={<ContentCopyIcon />} sx={{ borderRadius: 2, background: 'linear-gradient(135deg,var(--v4-cyan),var(--v4-cyan-deep))' }}>Kopieren</Button>
        </DialogActions>
      </Dialog>

      {/* Profile editor dialog */}
      <Dialog
        open={profileEditorOpen} onClose={() => setProfileEditorOpen(false)}
        fullWidth maxWidth="md"
        PaperProps={{ sx: { ...V4_CARD, backdropFilter: 'blur(20px)', maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ color: 'var(--v4-text)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Nährstoffprofil bearbeiten
          <IconButton onClick={() => setProfileEditorOpen(false)} sx={{ color: 'rgba(var(--v4-muted-rgb),0.4)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ overflowY: 'auto' }}>
          {profileDraft && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Profilname" size="small" fullWidth
                value={profileDraft.name}
                onChange={e => setProfileDraft(d => ({ ...d, name: e.target.value }))}
                InputLabelProps={{ sx: { color: 'rgba(var(--v4-cyan-rgb),0.6)' } }}
                inputProps={{ style: { color: 'var(--v4-text)' } }}
                sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.2)' } }}
              />
              <TextField
                label="Beschreibung" size="small" fullWidth multiline rows={2}
                value={profileDraft.description || ''}
                onChange={e => setProfileDraft(d => ({ ...d, description: e.target.value }))}
                InputLabelProps={{ sx: { color: 'rgba(var(--v4-cyan-rgb),0.6)' } }}
                inputProps={{ style: { color: 'var(--v4-text)' } }}
                sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.2)' } }}
              />

              <Divider sx={{ borderColor: 'rgba(var(--v4-cyan-rgb),0.1)' }} />

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography sx={{ color: 'var(--v4-cyan)', fontWeight: 800, fontSize: '0.85rem' }}>
                  Wochenplan ({profileDraft.weeks?.length || 0} Wochen)
                </Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addWeek}
                  sx={{ color: 'var(--v4-cyan)', borderColor: 'rgba(var(--v4-cyan-rgb),0.3)', borderRadius: 2 }} variant="outlined">
                  Woche hinzufügen
                </Button>
              </Stack>

              {(profileDraft.weeks || []).map((week, wi) => (
                <Paper key={wi} sx={{
                  background: 'rgba(var(--v4-cyan-rgb),0.04)',
                  border: '1px solid rgba(var(--v4-cyan-rgb),0.1)',
                  borderRadius: '10px', p: 1.5,
                }}>
                  <Typography sx={{ color: 'var(--v4-cyan)', fontWeight: 700, fontSize: '0.78rem', mb: 1 }}>
                    Woche {week.week_number}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                    <TextField
                      label="EC" size="small" type="number"
                      value={week.target_ec ?? ''}
                      onChange={e => updateWeekField(wi, 'target_ec', e.target.value === '' ? null : parseFloat(e.target.value))}
                      InputLabelProps={{ sx: { color: 'rgba(var(--v4-cyan-rgb),0.5)', fontSize: '0.75rem' } }}
                      inputProps={{ style: { color: 'var(--v4-text)', fontSize: '0.8rem' }, step: 0.1 }}
                      sx={{ width: 90, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.15)' } }}
                    />
                    <TextField
                      label="pH" size="small" type="number"
                      value={week.target_ph ?? ''}
                      onChange={e => updateWeekField(wi, 'target_ph', e.target.value === '' ? null : parseFloat(e.target.value))}
                      InputLabelProps={{ sx: { color: 'rgba(var(--v4-cyan-rgb),0.5)', fontSize: '0.75rem' } }}
                      inputProps={{ style: { color: 'var(--v4-text)', fontSize: '0.8rem' }, step: 0.1 }}
                      sx={{ width: 90, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.15)' } }}
                    />
                    <TextField
                      label="Wasser (L)" size="small" type="number"
                      value={week.water_l ?? ''}
                      onChange={e => updateWeekField(wi, 'water_l', e.target.value === '' ? null : parseFloat(e.target.value))}
                      InputLabelProps={{ sx: { color: 'rgba(var(--v4-cyan-rgb),0.5)', fontSize: '0.75rem' } }}
                      inputProps={{ style: { color: 'var(--v4-text)', fontSize: '0.8rem' }, step: 0.5 }}
                      sx={{ width: 110, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.15)' } }}
                    />
                    <TextField
                      label="Notiz" size="small"
                      value={week.note || ''}
                      onChange={e => updateWeekField(wi, 'note', e.target.value)}
                      InputLabelProps={{ sx: { color: 'rgba(var(--v4-cyan-rgb),0.5)', fontSize: '0.75rem' } }}
                      inputProps={{ style: { color: 'var(--v4-text)', fontSize: '0.8rem' } }}
                      sx={{ flex: 1, minWidth: 120, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.15)' } }}
                    />
                  </Stack>

                  {/* Products */}
                  <Stack spacing={0.5}>
                    {(week.products || []).map((prod, pi) => (
                      <Stack key={pi} direction="row" spacing={1} alignItems="center">
                        <TextField
                          placeholder="Produktname" size="small"
                          value={prod.product_name}
                          onChange={e => updateProductField(wi, pi, 'product_name', e.target.value)}
                          inputProps={{ style: { color: 'var(--v4-text)', fontSize: '0.78rem' } }}
                          sx={{ flex: 1, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.1)' } }}
                        />
                        <TextField
                          placeholder="ml/L" size="small" type="number"
                          value={prod.ml_per_l}
                          onChange={e => updateProductField(wi, pi, 'ml_per_l', parseFloat(e.target.value) || 0)}
                          inputProps={{ style: { color: 'var(--v4-text)', fontSize: '0.78rem' }, step: 0.5 }}
                          sx={{ width: 80, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.1)' } }}
                        />
                        <IconButton size="small" onClick={() => removeProduct(wi, pi)} sx={{ color: 'rgba(var(--v4-red-soft-rgb),0.6)' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                    <Button
                      size="small" startIcon={<AddIcon />}
                      onClick={() => addProduct(wi)}
                      sx={{ color: 'rgba(var(--v4-cyan-rgb),0.6)', fontSize: '0.72rem', alignSelf: 'flex-start', mt: 0.25 }}
                    >
                      Produkt
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setProfileEditorOpen(false)} variant="outlined" size="small" sx={{ borderRadius: 2, borderColor: 'rgba(var(--v4-cyan-rgb),0.2)', color: 'rgba(var(--v4-muted-rgb),0.7)' }}>Abbrechen</Button>
          <Button onClick={handleSaveProfile} variant="contained" size="small" startIcon={<SaveIcon />} disabled={saving} sx={{ borderRadius: 2, background: 'linear-gradient(135deg,var(--v4-cyan),var(--v4-cyan-deep))' }}>
            {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start date dialog */}
      <Dialog
        open={startDateDialogOpen} onClose={() => setStartDateDialogOpen(false)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { ...V4_CARD, backdropFilter: 'blur(20px)' } }}
      >
        <DialogTitle sx={{ color: 'var(--v4-text)' }}>Grow starten</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'rgba(var(--v4-muted-rgb),0.6)', mb: 2 }}>
            Lege den Starttermin des Grows fest. Die aktive Phase wird automatisch anhand des Datums bestimmt und die zugehörige Konfiguration eingespielt.
          </Typography>
          <TextField
            type="date" fullWidth size="small" label="Startdatum"
            value={startDateValue}
            onChange={e => setStartDateValue(e.target.value)}
            InputLabelProps={{ shrink: true, sx: { color: 'rgba(var(--v4-cyan-rgb),0.6)' } }}
            inputProps={{ style: { color: 'var(--v4-text)' } }}
            sx={{ '& .MuiOutlinedInput-root': { background: 'rgba(var(--v4-cyan-rgb),0.04)', borderRadius: '10px', '& fieldset': { borderColor: 'rgba(var(--v4-cyan-rgb),0.2)' } } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setStartDateDialogOpen(false)} variant="outlined" size="small" sx={{ borderRadius: 2, borderColor: 'rgba(var(--v4-cyan-rgb),0.2)', color: 'rgba(var(--v4-muted-rgb),0.7)' }}>
            Abbrechen
          </Button>
          <Button onClick={handleSetStartDate} variant="contained" size="small"
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg,var(--v4-green),#1a8000)', color: '#000', fontWeight: 700 }}
          >
            Grow starten
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
